import { useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import type { SanctionMatch } from "../utils/types";

type SanctionsScreeningPaneProps = {
  open: boolean;
  onClose: () => void;
  companyName?: string;
  matches?: SanctionMatch[];
};

const toTitleCase = (value?: string): string =>
  (value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatScore = (score?: number): string => {
  if (score === undefined || score === null || Number.isNaN(Number(score))) return "-";
  return `${Number(score)}%`;
};

const Field = ({ label, value }: { label: string; value?: string }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5">
    <span className="tw-text-xs tw-leading-4 tw-text-gray-700">{label}</span>
    <span className="tw-text-xs tw-font-medium tw-leading-4 tw-text-gray-900 tw-break-words">{value || "-"}</span>
  </div>
);

const SanctionsScreeningPane = ({ open, onClose, companyName, matches = [] }: SanctionsScreeningPaneProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  return (
    <>
      {open ? <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-bg-[#1b1c1e]/50 tw-backdrop-blur-[9px]" onClick={onClose} /> : null}
      <div className={`tw-fixed tw-top-0 tw-right-0 tw-h-[calc(100vh-50px)] tw-my-12 tw-bg-white tw-shadow-sideDrawer tw-border-t tw-border-l tw-border-b tw-border-solid tw-border-[#e2e2e2] tw-transition-all tw-duration-250 tw-ease-in-out tw-rounded-l-lg tw-z-[10000] ${open ? "tw-translate-x-0" : "tw-w-0 tw-translate-x-full"}`}>
        {open ? (
          <div className="tw-relative tw-flex tw-h-full tw-w-[724px] tw-max-w-[600px] tw-flex-col">
            <div className="tw-absolute tw-top-[5px] -tw-left-9 tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-l-lg tw-bg-white tw-shadow-sideDrawer tw-text-gray-500" onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
                <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
              </svg>
            </div>

            <div className="tw-rounded-tl-lg tw-bg-white tw-px-4 tw-py-3 tw-shadow-[0_1px_0_0_theme(colors.gray.200)]">
              <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-leading-5 tw-tracking-[-0.07px] tw-text-gray-900">Sanction screening on companies</p>
            </div>

            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-6 tw-overflow-auto tw-px-4 tw-pt-4 tw-pb-4">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-flex tw-flex-col">
                  <span className="tw-text-xs tw-font-semibold tw-leading-4 tw-text-gray-900">{companyName ? toTitleCase(companyName) : "Unknown"}</span>
                  {matches.length ? (
                    <span className="tw-text-xs tw-leading-4 tw-text-black">{`${matches.length} ${matches.length === 1 ? "match" : "matches"} found`}</span>
                  ) : (
                    <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-leading-4 tw-text-black">
                      No match found on any sanctions list
                      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#0bd351">
                        <path d="m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" />
                      </svg>
                    </span>
                  )}
                </div>

                {matches.map((item) => (
                  <div key={`${item.matched_against || "match"}-${item.sanctioning_programme || ""}-${item.sanctioning_country || ""}-${item.match_score ?? ""}-${item.citation_url || ""}`} className="tw-flex tw-flex-col tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-[#ffe4e4] tw-bg-[#fffcfc] tw-p-3">
                    <span className="tw-rounded-xl tw-bg-[#ffeded] tw-px-1 tw-text-xs tw-leading-4 tw-text-[#b42318]">{`${formatScore(item.match_score)} match score`}</span>
                    <Field label="Matched Against" value={item.matched_against} />
                    <Field label="Sanctioning Program" value={item.sanctioning_programme} />
                    <div className="tw-flex tw-flex-col tw-gap-0.5">
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-700">Sanctioning Country</span>
                      <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-leading-4 tw-text-gray-900">
                        {item.sanctioning_country || "-"}
                        {item.sanctioning_country_code ? <ReactCountryFlag countryCode={item.sanctioning_country_code} svg className="tw-shrink-0" /> : null}
                      </span>
                    </div>
                    {item.citation_url ? (
                      <a href={item.citation_url} target="_blank" rel="noopener noreferrer" className="tw-flex tw-items-center tw-text-xs tw-leading-4 tw-text-primary-purple-600 hover:tw-text-primary-purple-500">
                        Access Citation
                        <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                          <path d="M216-160l-56-56 464-464H360v-80h400v400h-80v-264L216-160Z" />
                        </svg>
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default SanctionsScreeningPane;
